import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputLabel,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ErrorPage from "../Components/ErrorPage";
import PayrollAdd from "./PayrollAdd";
import PayrollEdit from "./PayrollEdit";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { printPayroll } from "../utils/printPayroll";

const shopPrimaryGreen = "#355d31";
const shopPrimaryGreenDark = "#274923";
const shopPrimaryGreenLight = "#5b8a42";

const PayrollIndex = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const auth = useAuth();
  const token = auth?.user?.token;
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, index) => currentYear - index);
  const monthOptions = [
    { value: 1, label: t("payroll.january", "January") },
    { value: 2, label: t("payroll.february", "February") },
    { value: 3, label: t("payroll.march", "March") },
    { value: 4, label: t("payroll.april", "April") },
    { value: 5, label: t("payroll.may", "May") },
    { value: 6, label: t("payroll.june", "June") },
    { value: 7, label: t("payroll.july", "July") },
    { value: 8, label: t("payroll.august", "August") },
    { value: 9, label: t("payroll.september", "September") },
    { value: 10, label: t("payroll.october", "October") },
    { value: 11, label: t("payroll.november", "November") },
    { value: 12, label: t("payroll.december", "December") },
  ];

  // Pagination and filtering states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  // Fetch payroll records with search and pagination.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "payroll",
      searchTerm,
      selectedEmployee,
      selectedYear,
      selectedMonth,
      page,
      rowsPerPage,
    ],
    `shop/payrolls/?search=${searchTerm}&employee=${selectedEmployee}&year=${selectedYear}&month=${selectedMonth}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Fetch employees for dropdown filter
  const { data: employeesData } = useFetchObjects(
    ["employees"],
    "shop/employees/?page_size=1000",
    token
  );
  const employees = Array.isArray(employeesData)
    ? employeesData
    : employeesData?.results || [];

  // Handle delete function
  const handleDelete = (id) => {
    setConfirmDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/shop/payrolls/${confirmDialog.id}/`,
        {
          headers:
            auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
        }
      );
      refetch();
      setConfirmDialog({ open: false, id: null });
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // Print function for individual payroll
  const handlePrintPayroll = (payroll) => {
    const printWindow = window.open('', '_blank');
    const formattedNow = new Date().toLocaleString();
    const formattedPaymentDate = payroll.payment_date
      ? new Date(payroll.payment_date).toLocaleString()
      : "N/A";
    const printContent = `
      <html>
        <head>
          <title>Payroll Print</title>
          <style>
            @media print {
              @page { margin: 10mm; size: A4 portrait; }
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              background: #f5f7fb;
              color: #132238;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 14px;
            }
            .page {
              width: 100%;
              min-height: 100vh;
              background: #f5f7fb;
            }
            .topbar {
              height: 56px;
              background: ${shopPrimaryGreen};
              color: #fff;
              display: flex;
              align-items: center;
              padding: 0 22px;
              box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
            }
            .topbar-title {
              font-size: 18px;
              font-weight: 700;
              letter-spacing: -0.01em;
            }
            .content {
              padding: 28px 26px 24px;
            }
            .header-row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
              margin-bottom: 18px;
            }
            .title {
              margin: 0 0 10px;
              font-size: 28px;
              font-weight: 500;
              color: #132238;
            }
            .subtitle {
              margin: 0;
              color: #5f6f86;
              font-size: 14px;
            }
            .action-chip {
              background: ${shopPrimaryGreen};
              color: #fff;
              padding: 12px 18px;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 700;
              box-shadow: 0 6px 18px rgba(53, 93, 49, 0.22);
              white-space: nowrap;
            }
            .filters {
              display: grid;
              grid-template-columns: 1.6fr 1fr 1fr 1fr;
              gap: 14px;
              margin-bottom: 18px;
            }
            .filter-box {
              background: #fff;
              border: 1px solid #d7e1ee;
              border-radius: 8px;
              min-height: 64px;
              padding: 12px 16px;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }
            .filter-label {
              color: #7b8798;
              font-size: 12px;
              margin-bottom: 4px;
            }
            .filter-value {
              color: #132238;
              font-size: 16px;
              font-weight: 500;
            }
            .table-card {
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 10px;
              overflow: hidden;
              box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            thead tr {
              background: ${shopPrimaryGreen};
            }
            th {
              color: #fff;
              text-align: left;
              font-size: 14px;
              font-weight: 700;
              padding: 18px 16px;
            }
            td {
              padding: 18px 16px;
              border-bottom: 1px solid #e7edf5;
              color: #132238;
              vertical-align: top;
            }
            tr:last-child td {
              border-bottom: none;
            }
            .summary-row {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 14px;
              margin-top: 18px;
            }
            .summary-card {
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 8px;
              padding: 14px 16px;
            }
            .summary-title {
              color: #7b8798;
              font-size: 12px;
              margin-bottom: 6px;
            }
            .summary-value {
              color: #132238;
              font-size: 18px;
              font-weight: 700;
            }
            .notes-card {
              margin-top: 18px;
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 8px;
              padding: 16px;
            }
            .notes-title {
              font-size: 14px;
              font-weight: 700;
              margin-bottom: 8px;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              color: #7b8798;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="topbar">
              <div class="topbar-title">Professional Dashboard</div>
            </div>
            <div class="content">
              <div class="header-row">
                <div>
                  <h1 class="title">Payroll List</h1>
                  <p class="subtitle">Data loaded on ${formattedNow}</p>
                </div>
                <div class="action-chip">PAYROLL DETAILS</div>
              </div>

              <div class="filters">
                <div class="filter-box">
                  <div class="filter-label">Employee</div>
                  <div class="filter-value">${payroll?.employee_details?.name || 'N/A'}</div>
                </div>
                <div class="filter-box">
                  <div class="filter-label">Year</div>
                  <div class="filter-value">${payroll.year || 'N/A'}</div>
                </div>
                <div class="filter-box">
                  <div class="filter-label">Month</div>
                  <div class="filter-value">${payroll.month || 'N/A'}</div>
                </div>
              </div>

              <div class="table-card">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Employee</th>
                      <th>Amount</th>
                      <th>Currency</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Payment Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>${payroll.id || 'N/A'}</td>
                      <td>${payroll?.employee_details?.name || 'N/A'}</td>
                      <td>${payroll.amount || '0.00'}</td>
                      <td>${payroll?.currency_details?.title || 'N/A'}</td>
                      <td>${payroll.year || 'N/A'}</td>
                      <td>${payroll.month || 'N/A'}</td>
                      <td>${formattedPaymentDate}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="summary-row">
                <div class="summary-card">
                  <div class="summary-title">Amount</div>
                  <div class="summary-value">${payroll.amount || '0.00'}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Currency</div>
                  <div class="summary-value">${payroll?.currency_details?.title || 'N/A'}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Payment Date</div>
                  <div class="summary-value" style="font-size: 15px;">${formattedPaymentDate}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Record Status</div>
                  <div class="summary-value" style="color: ${shopPrimaryGreen};">Payroll</div>
                </div>
              </div>

              <div class="notes-card">
                <div class="notes-title">Notes</div>
                <div>${payroll.notes || 'No notes available for this payroll record.'}</div>
              </div>

              <div class="footer">Generated from the payroll dashboard print view</div>
            </div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    refetch();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    page,
    rowsPerPage,
    refetch,
  ]);

  // Determine payroll list and total count (using DRF paginated response)
  const payrolls = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : payrolls.length;

  if (isError) {
    return <ErrorPage message={t("payroll.failedToLoadPayrolls")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setPage(1);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setPage(1);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setPage(1);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: isDarkMode ? "#121212" : "#f5f7fb",
        color: isDarkMode ? "#f3f4f6" : "#132238",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 1.5,
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: isDarkMode ? "#f9fafb" : "#132238",
            letterSpacing: "-0.02em",
          }}
        >
          {t("payroll.payrollList")}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpenAdd(true)}
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            minWidth: 142,
            height: 42,
            px: 2.5,
            borderRadius: 1.5,
            boxShadow: "0 6px 18px rgba(53, 93, 49, 0.22)",
            fontWeight: 700,
            backgroundColor: shopPrimaryGreen,
            "&:hover": {
              backgroundColor: shopPrimaryGreenDark,
            },
          }}
        >
          {t("payroll.addPayroll")}
        </Button>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          color: isDarkMode ? "#a9b4c4" : "#5f6f86",
          mb: 2.5,
          fontWeight: 400,
        }}
      >
        {t("payroll.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search and Filter Fields */}
      <Box
        sx={{
          mb: 2.5,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr 1fr" },
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("payroll.searchPayrolls")}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            borderRadius: 1,
            "& .MuiInputBase-input": {
              color: isDarkMode ? "#f3f4f6" : "#132238",
            },
            "& .MuiInputBase-input::placeholder": {
              opacity: 1,
              color: isDarkMode ? "#8d98a8" : "#a4adba",
            },
            "& .MuiInputAdornment-root": {
              color: isDarkMode ? "#a9b4c4" : "#6f7d90",
            },
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
              "& fieldset": {
                borderColor: isDarkMode ? "#3a3a3a" : "#d7e1ee",
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? "#557a4b" : shopPrimaryGreenLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: shopPrimaryGreenLight,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: isDarkMode ? "#a9b4c4" : "#6f7d90",
                    fontSize: 28,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            borderRadius: 1,
            "& .MuiInputBase-input": {
              color: isDarkMode ? "#f3f4f6" : "#132238",
            },
            "& .MuiInputLabel-root": {
              color: isDarkMode ? "#a9b4c4" : "#5f6f86",
            },
            "& .MuiSelect-icon": {
              color: isDarkMode ? "#d1d5db" : "#6f7d90",
            },
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
              "& fieldset": {
                borderColor: isDarkMode ? "#3a3a3a" : "#d7e1ee",
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? "#557a4b" : shopPrimaryGreenLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: shopPrimaryGreenLight,
              },
            },
          }}
        >
          <Select
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            displayEmpty
            placeholder={t("payroll.selectEmployee")}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {employees.map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            borderRadius: 1,
            "& .MuiInputBase-input": {
              color: isDarkMode ? "#f3f4f6" : "#132238",
            },
            "& .MuiInputLabel-root": {
              color: isDarkMode ? "#a9b4c4" : "#5f6f86",
            },
            "& .MuiSelect-icon": {
              color: isDarkMode ? "#d1d5db" : "#6f7d90",
            },
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
              "& fieldset": {
                borderColor: isDarkMode ? "#3a3a3a" : "#d7e1ee",
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? "#557a4b" : shopPrimaryGreenLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: shopPrimaryGreenLight,
              },
            },
          }}
        >
          <InputLabel id="payroll-year-label">
            {t("payroll.year", "Year")} *
          </InputLabel>
          <Select
            labelId="payroll-year-label"
            value={selectedYear}
            onChange={handleYearChange}
            label={`${t("payroll.year", "Year")} *`}
          >
            <MenuItem value="">
              <em>{t("payroll.allYears", "All Years")}</em>
            </MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            borderRadius: 1,
            "& .MuiInputBase-input": {
              color: isDarkMode ? "#f3f4f6" : "#132238",
            },
            "& .MuiInputLabel-root": {
              color: isDarkMode ? "#a9b4c4" : "#5f6f86",
            },
            "& .MuiSelect-icon": {
              color: isDarkMode ? "#d1d5db" : "#6f7d90",
            },
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
              "& fieldset": {
                borderColor: isDarkMode ? "#3a3a3a" : "#d7e1ee",
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? "#557a4b" : shopPrimaryGreenLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: shopPrimaryGreenLight,
              },
            },
          }}
        >
          <InputLabel id="payroll-month-label">
            {t("payroll.month", "Month")} *
          </InputLabel>
          <Select
            labelId="payroll-month-label"
            value={selectedMonth}
            onChange={handleMonthChange}
            label={`${t("payroll.month", "Month")} *`}
          >
            <MenuItem value="">
              <em>{t("payroll.allMonths", "All Months")}</em>
            </MenuItem>
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Payroll Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          borderRadius: 1.5,
          overflow: "hidden",
          boxShadow: isDarkMode
            ? "0 10px 24px rgba(0, 0, 0, 0.32)"
            : "0 2px 10px rgba(15, 23, 42, 0.08)",
          border: `1px solid ${isDarkMode ? "#2f2f2f" : "#dbe3ef"}`,
          backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
        }}
      >
        <Table sx={{ minWidth: 750 }} aria-label="payroll table">
          <TableHead>
            <TableRow sx={{ backgroundColor: shopPrimaryGreen }}>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.payrollId")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.employee")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.amount")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.currency")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.account", "Account")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.exchangeRate", "Exchange Rate")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.convertedAmount", "Converted Amount")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.year")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.month")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.paymentDate")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.notes")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Employee",
                      "Amount",
                      "Currency",
                      "Account",
                      "Exchange Rate",
                      "Converted Amount",
                      "Year",
                      "Month",
                      "Payment Date",
                      "Notes",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payrolls.map((payroll, index) => (
                  <TableRow
                    key={payroll.id}
                    hover
                    sx={{
                      backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
                      "& td": {
                        py: 2.5,
                        borderBottom: `1px solid ${
                          isDarkMode ? "#2f2f2f" : "#e7edf5"
                        }`,
                      },
                      "&:hover": {
                        backgroundColor: isDarkMode ? "#232323" : "#f8fbff",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: isDarkMode ? "#f3f4f6" : "#132238",
                        fontWeight: 500,
                      }}
                    >
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll?.employee_details?.name ||
                        t("payroll.unknownEmployee")}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.amount || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll?.currency_details?.title || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll?.account_details?.name || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.exchange_rate || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.converted_amount || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.year || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.month || "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.payment_date
                        ? new Date(payroll.payment_date).toLocaleString()
                        : "--"}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? "#e5e7eb" : "#132238" }}>
                      {payroll.notes || "--"}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            minWidth: 122,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                            borderColor: shopPrimaryGreen,
                            color: isDarkMode ? "#d1d5db" : shopPrimaryGreen,
                            "&:hover": {
                              borderColor: shopPrimaryGreenDark,
                              backgroundColor: isDarkMode
                                ? "rgba(91, 138, 66, 0.15)"
                                : "rgba(53, 93, 49, 0.08)",
                            },
                          }}
                          onClick={() => {
                            setEditData(payroll);
                            setOpenEdit(true);
                          }}
                        >
                          {t("payroll.editPayroll")}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PrintIcon />}
                          sx={{
                            minWidth: 92,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                            borderColor: shopPrimaryGreen,
                            color: isDarkMode ? "#d1d5db" : shopPrimaryGreen,
                            "&:hover": {
                              borderColor: shopPrimaryGreenDark,
                              backgroundColor: isDarkMode
                                ? "rgba(91, 138, 66, 0.15)"
                                : "rgba(53, 93, 49, 0.08)",
                            },
                          }}
                          onClick={() => handlePrintPayroll(payroll)}
                        >
                          Print
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          sx={{
                            minWidth: 78,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleDelete(payroll.id)}
                        >
                          {t("payroll.delete")}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="18px 10px 6px"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: isDarkMode ? "#e5e7eb" : "#132238",
              fontSize: 16,
            }}
          >
            {t("payroll.rowsPerPage", "Rows Per Page")}
          </Typography>
          <FormControl
            sx={{
              minWidth: 84,
              backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                color: isDarkMode ? "#f3f4f6" : "#132238",
                "& fieldset": {
                  borderColor: isDarkMode ? "#3a3a3a" : "#d7e1ee",
                },
                "&:hover fieldset": {
                  borderColor: isDarkMode ? "#557a4b" : shopPrimaryGreenLight,
                },
                "&.Mui-focused fieldset": {
                  borderColor: shopPrimaryGreenLight,
                },
              },
              "& .MuiSelect-icon": {
                color: isDarkMode ? "#d1d5db" : "#6f7d90",
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          shape="rounded"
          sx={{
            "& .MuiPaginationItem-root": {
              color: isDarkMode ? "#d1d5db" : "#132238",
              borderColor: isDarkMode ? "#3a3a3a" : undefined,
              borderRadius: "50%",
            },
            "& .Mui-selected": {
              backgroundColor: shopPrimaryGreen + " !important",
              color: "#fff",
            },
          }}
        />
      </Grid>

      {openAdd && (
        <PayrollAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onSaved={() => {
            setOpenAdd(false);
            refetch();
          }}
          token={token}
        />
      )}

      {openEdit && (
        <PayrollEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          onSaved={() => {
            setOpenEdit(false);
            setEditData(null);
            refetch();
          }}
          editData={editData}
          token={token}
        />
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1b1b1b" : "#fff",
            color: isDarkMode ? "#f3f4f6" : "#132238",
            border: `1px solid ${isDarkMode ? "#2f2f2f" : "#dbe3ef"}`,
          },
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? "#f3f4f6" : "#132238" }}>
          {t("payroll.confirmDelete")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDarkMode ? "#a9b4c4" : "#5f6f86" }}>
            {t("payroll.confirmDeleteMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, id: null })}
            sx={{ color: isDarkMode ? "#d1d5db" : undefined }}
          >
            {t("payroll.cancel")}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t("payroll.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollIndex;
